import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { StatsCard, LoadingState } from '../../components/admin/shared';
import educentialApi from '../../api/educentialApi';
import {
  Users,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  UserPlus,
  Activity,
  Globe,
  Award,
  Bell
} from 'lucide-react';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load stats
      const statsResponse = await educentialApi.get('/api/admin/stats');
      setStats(statsResponse.data);

      // Try to load activity, but don't fail if endpoint doesn't exist
      try {
        const activityResponse = await educentialApi.get('/api/admin/activity?limit=10');
        setActivity(activityResponse.data || []);
      } catch (activityErr) {
        // Activity endpoint doesn't exist yet, just show empty
        console.log('Activity endpoint not available yet');
        setActivity([]);
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard data');

      // If unauthorized or forbidden, redirect to login
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard Overview">
        <LoadingState message="Loading dashboard..." />
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Dashboard Overview">
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center space-x-3 text-red-600">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const getActivityIcon = (type) => {
    const icons = {
      'user_registered': UserPlus,
      'application_submitted': FileText,
      'application_updated': Activity,
      'document_uploaded': FileText,
      'offer_secured': Award,
      'default': Bell
    };
    return icons[type] || icons.default;
  };

  const getActivityColor = (type) => {
    const colors = {
      'user_registered': 'text-green-600 bg-green-50',
      'application_submitted': 'text-blue-600 bg-blue-50',
      'application_updated': 'text-yellow-600 bg-yellow-50',
      'document_uploaded': 'text-purple-600 bg-purple-50',
      'offer_secured': 'text-teal-600 bg-teal-50',
      'default': 'text-gray-600 bg-gray-50'
    };
    return colors[type] || colors.default;
  };

  return (
    <AdminLayout title="Dashboard Overview">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Platform Overview</h2>
            <p className="text-gray-600 mt-1">Monitor all services, users, and applications across the platform</p>
          </div>
          <button
            onClick={() => navigate('/admin/applications')}
            className="mt-4 sm:mt-0 px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            View Applications
          </button>
        </div>

        {/* User Metrics */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatsCard
              title="Total Users"
              value={stats?.users?.total || 0}
              icon={Users}
              trend={stats?.users?.growthRate > 0 ? `+${stats?.users?.growthRate}%` : null}
            />
            <StatsCard
              title="Students"
              value={stats?.users?.byRole?.STUDENT || 0}
              icon={Users}
            />
            <StatsCard
              title="Recent Signups"
              value={stats?.users?.recentSignups || 0}
              icon={UserPlus}
              subtitle="Last 7 days"
            />
            <StatsCard
              title="Active Users"
              value={stats?.users?.activeUsers || 0}
              icon={Activity}
              subtitle="Last 30 days"
            />
          </div>
        </div>

        {/* Application Metrics */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatsCard
              title="Total Applications"
              value={stats?.applications?.total || 0}
              icon={FileText}
            />
            <StatsCard
              title="Submitted"
              value={stats?.applications?.byStatus?.SUBMITTED || 0}
              icon={FileText}
            />
            <StatsCard
              title="Under Review"
              value={stats?.applications?.byStatus?.UNDER_REVIEW || 0}
              icon={Clock}
            />
            <StatsCard
              title="Completed"
              value={stats?.applications?.byStatus?.COMPLETED || 0}
              icon={CheckCircle}
            />
            <StatsCard
              title="Pending Docs"
              value={stats?.documents?.pending || 0}
              icon={AlertCircle}
            />
          </div>
        </div>

        {/* Service Overview */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Overview</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Globe className="w-5 h-5 text-green-600" />
                <span className="px-2 py-0.5 bg-green-200 text-green-800 text-xs font-medium rounded-full">
                  Active
                </span>
              </div>
              <p className="text-sm font-medium text-gray-900">Overseas Education</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {stats?.applications?.byService?.overseas || 0}
              </p>
              <p className="text-xs text-gray-600 mt-1">applications</p>
            </div>

            {[
              { name: 'Career Counseling', key: 'career' },
              { name: 'Skill Development', key: 'skills' },
              { name: 'Test Preparation', key: 'test-prep' },
              { name: 'Scholarships', key: 'scholarships' }
            ].map((service) => (
              <div key={service.key} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <Globe className="w-5 h-5 text-gray-400" />
                  <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs font-medium rounded-full">
                    Coming Soon
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-600">{service.name}</p>
                <p className="text-2xl font-bold text-gray-400 mt-1">-</p>
                <p className="text-xs text-gray-500 mt-1">not active</p>
              </div>
            ))}
          </div>
          <button
            onClick={() => navigate('/admin/applications?feature=overseas')}
            className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            View all services →
          </button>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
              <Activity className="w-5 h-5 text-gray-400" />
            </div>

            {activity.length > 0 ? (
              <div className="space-y-3">
                {activity.slice(0, 8).map((item, index) => {
                  const Icon = getActivityIcon(item.type);
                  const colorClass = getActivityColor(item.type);

                  return (
                    <div key={index} className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${colorClass}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{item.description}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {new Date(item.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No recent activity</p>
            )}
          </div>

          {/* User Growth Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">User Growth (Last 30 Days)</h3>

            {stats?.users?.growthData && stats.users.growthData.length > 0 ? (
              <div className="space-y-4">
                {/* Simple bar chart */}
                <div className="relative h-48">
                  <div className="absolute inset-0 flex items-end justify-between space-x-1">
                    {stats.users.growthData.slice(-30).map((day, index) => {
                      const maxUsers = Math.max(...stats.users.growthData.map(d => d.newUsers));
                      const height = maxUsers > 0 ? (day.newUsers / maxUsers) * 100 : 0;

                      return (
                        <div key={index} className="flex-1 flex flex-col items-center group">
                          <div
                            className="w-full bg-blue-500 hover:bg-blue-600 transition-colors rounded-t cursor-pointer relative"
                            style={{ height: `${height}%` }}
                            title={`${new Date(day.date).toLocaleDateString()}: ${day.newUsers} new users`}
                          >
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                              {day.newUsers} users
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-xs text-gray-500">Total New</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {stats.users.growthData.reduce((sum, d) => sum + d.newUsers, 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Avg/Day</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {(stats.users.growthData.reduce((sum, d) => sum + d.newUsers, 0) / stats.users.growthData.length).toFixed(1)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Peak Day</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {Math.max(...stats.users.growthData.map(d => d.newUsers))}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No growth data available</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/admin/users')}
              className="flex items-center space-x-3 p-4 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors text-left"
            >
              <Users className="w-6 h-6 text-blue-600 flex-shrink-0" />
              <span className="text-gray-900 font-medium">Manage Users</span>
            </button>

            <button
              onClick={() => navigate('/admin/applications')}
              className="flex items-center space-x-3 p-4 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors text-left"
            >
              <FileText className="w-6 h-6 text-green-600 flex-shrink-0" />
              <span className="text-gray-900 font-medium">Review Applications</span>
            </button>

            <button
              onClick={() => navigate('/admin/reports')}
              className="flex items-center space-x-3 p-4 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors text-left"
            >
              <TrendingUp className="w-6 h-6 text-purple-600 flex-shrink-0" />
              <span className="text-gray-900 font-medium">View Reports</span>
            </button>

            <button
              onClick={() => navigate('/admin/operations/notifications')}
              className="flex items-center space-x-3 p-4 bg-yellow-50 hover:bg-yellow-100 border border-yellow-200 rounded-lg transition-colors text-left"
            >
              <Bell className="w-6 h-6 text-yellow-600 flex-shrink-0" />
              <span className="text-gray-900 font-medium">Send Notifications</span>
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
